import { useEffect } from "react";

/**
 * **Lo que puede estar sin guardar**, para la actualización de la aplicación (owner, 15-oct #0b): con
 * algo pendiente, una versión nueva no recarga la página sola (`PwaUpdates` avisa «Hay una versión
 * nueva · Actualizar»). Vive aparte de `PwaUpdates` para que una pantalla lo use sin cargar el registro
 * del service worker.
 */

/** Lo marcado como pendiente por una pantalla (`useCambiosSinGuardar`). */
const pendientes = new Set<symbol>();
/** El último campo en que se escribió: si sigue en pantalla, puede tener algo sin guardar. */
let ultimoEscrito: EventTarget | null = null;

/** Mientras `pendiente` sea verdadero, una versión nueva no recarga sola: avisa. */
export function useCambiosSinGuardar(pendiente: boolean) {
  useEffect(() => {
    if (!pendiente) return;
    const id = Symbol("sin guardar");
    pendientes.add(id);
    return () => { pendientes.delete(id); };
  }, [pendiente]);
}

/** Anota en qué campo se escribió (`input` y `change`, en captura); devuelve cómo dejar de anotar. */
export function vigilarLoEscrito(): () => void {
  const escrito = (e: Event) => { ultimoEscrito = e.target; };
  document.addEventListener("input", escrito, true);
  document.addEventListener("change", escrito, true);
  return () => {
    document.removeEventListener("input", escrito, true);
    document.removeEventListener("change", escrito, true);
  };
}

const editable = (el: unknown) =>
  el instanceof HTMLElement && (el.isContentEditable || (el.matches("input, textarea, select") && !(el as HTMLInputElement).disabled));

/**
 * Si algo en pantalla puede tener cambios sin guardar: una pantalla que lo marcó, un campo con el foco o
 * el último en que se escribió, si sigue en pantalla (al guardar, la hoja lateral se cierra y el campo
 * sale). Ante la duda, avisa: recargar de más se arregla con un clic; perder lo escrito, no.
 */
export function hayCambiosSinGuardar(): boolean {
  return pendientes.size > 0 || editable(document.activeElement)
    || (ultimoEscrito instanceof Node && ultimoEscrito.isConnected && editable(ultimoEscrito));
}
